/**
 * RoleIcon Component - Unified Role Display for Delivery Logs
 * 
 * Replaces emoji-based role indicators with Lucide icons.
 * Used in delivery log components and demo data rendering.
 */

import { User, Truck, Settings } from "lucide-react";
import { cn } from "@/lib/utils";

export type LogRole = "manager" | "courier" | "system";

interface RoleIconProps {
  role: LogRole;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const ROLE_CONFIG: Record<LogRole, {
  icon: React.ElementType;
  colorClass: string;
}> = {
  manager: {
    icon: User,
    colorClass: "text-primary",
  },
  courier: {
    icon: Truck,
    colorClass: "text-purple-500 dark:text-purple-400",
  },
  system: {
    icon: Settings,
    colorClass: "text-muted-foreground",
  },
};

const SIZE_CONFIG = {
  sm: "h-3 w-3",
  md: "h-3.5 w-3.5",
  lg: "h-4 w-4",
};

export function RoleIcon({ role, size = "sm", className }: RoleIconProps) {
  const config = ROLE_CONFIG[role];
  const Icon = config.icon;
  const sizeClass = SIZE_CONFIG[size];
  
  return (
    <Icon className={cn(sizeClass, config.colorClass, className)} />
  );
}
